import React, { useState } from 'react';
import { Claim } from '../api/claims';
import ComplexAIButton from './ComplexAIButton';
import './SendLetterModal.css';

interface SendLetterModalProps {
  isOpen: boolean;
  onClose: () => void;
  claim: Claim;
  onSend: (letterData: LetterData, generateOnly: boolean) => Promise<void>;
}

export interface LetterData {
  recipientEmail: string;
  recipientName: string;
  recipientAddressLine1: string;
  recipientAddressLine2: string;
  recipientPostcode: string;
  letterContent: string;
}

type RecipientType = 'lender' | 'client' | 'other';

interface MergeField {
  label: string;
  value: string;
  description: string;
}

const MERGE_FIELDS: MergeField[] = [
  { label: 'Claim Reference', value: '{ProclaimRef}', description: 'Proclaim reference number' },
  { label: 'First Name', value: '{FirstName}', description: 'Client first name' },
  { label: 'Surname', value: '{Surname}', description: 'Client surname' },
  { label: 'Full Name', value: '{FullName}', description: 'Client full name' },
  { label: 'Date of Birth', value: '{DOB}', description: 'Client date of birth' },
  { label: 'Current Date', value: '{CurrentDate}', description: "Today's date" },
  { label: 'Address Line 1', value: '{AddressLine1}', description: 'Client address line 1' },
  { label: 'Address Line 2', value: '{AddressLine2}', description: 'Client address line 2' },
  { label: 'Postcode', value: '{Postcode}', description: 'Client postcode' },
  { label: 'Lender Name', value: '{LenderName}', description: 'Finance lender name' },
  { label: 'Agreement Number', value: '{AgreementNumber}', description: 'Credit agreement number' },
  { label: 'Credit Start Date', value: '{CreditStartDate}', description: 'Credit agreement start date' },
];

const SendLetterModal: React.FC<SendLetterModalProps> = ({
  isOpen,
  onClose,
  claim,
  onSend
}) => {
  const [recipientType, setRecipientType] = useState<RecipientType>('lender');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientAddressLine1, setRecipientAddressLine1] = useState('');
  const [recipientAddressLine2, setRecipientAddressLine2] = useState('');
  const [recipientPostcode, setRecipientPostcode] = useState('');
  const [letterContent, setLetterContent] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generateOnly, setGenerateOnly] = useState(false);

  // Get predefined email and name based on recipient type
  const getPredefinedRecipient = (type?: RecipientType) => {
    const currentType = type || recipientType;
    switch (currentType) {
      case 'lender':
        return {
          email: '', // Lender email not available on client side
          name: claim.craFinanceLender || ''
        };
      case 'client':
        return {
          email: claim.email || '',
          name: `${claim.firstName || ''} ${claim.lastName || ''}`.trim()
        };
      default:
        return { email: '', name: '' };
    }
  };

  const handleRecipientTypeChange = (type: RecipientType) => {
    setRecipientType(type);
    if (type !== 'other') {
      const predefined = getPredefinedRecipient(type);
      setRecipientEmail(predefined.email);
      setRecipientName(predefined.name);
    } else {
      setRecipientEmail('');
      setRecipientName('');
    }
  };

  const insertMergeField = (fieldValue: string) => {
    // Insert at cursor position or at the end
    const textarea = document.getElementById('letter-content') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent =
        letterContent.substring(0, start) +
        fieldValue +
        letterContent.substring(end);
      setLetterContent(newContent);

      // Set cursor position after inserted field
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + fieldValue.length, start + fieldValue.length);
      }, 0);
    } else {
      setLetterContent(letterContent + fieldValue);
    }
  };

  const handleSend = async (isGenerateOnly: boolean) => {
    // Validate required fields (email optional for generate-only)
    if (!recipientName || !recipientAddressLine1 || !recipientPostcode || !letterContent) {
      setError('Please fill in all required fields');
      return;
    }

    // Email is required only if sending
    if (!isGenerateOnly && !recipientEmail) {
      setError('Email is required when sending the letter');
      return;
    }

    setSending(true);
    setError(null);

    try {
      await onSend({
        recipientEmail: recipientEmail || '',  // Empty string if generating only
        recipientName,
        recipientAddressLine1,
        recipientAddressLine2,
        recipientPostcode,
        letterContent
      }, isGenerateOnly);
      onClose();
    } catch (err: any) {
      console.error(`Failed to ${isGenerateOnly ? 'generate' : 'send'} letter:`, err);
      setError(err.message || `Failed to ${isGenerateOnly ? 'generate' : 'send'} letter`);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const predefinedRecipient = getPredefinedRecipient();

  return (
    <div className="send-letter-modal-overlay" onClick={onClose}>
      <div className="send-letter-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Send Letter - {claim.proclaimReference || claim.id}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          {/* Recipient Type Selector */}
          <div className="form-group">
            <label htmlFor="recipient-type">Recipient Type *</label>
            <select
              id="recipient-type"
              value={recipientType}
              onChange={(e) => handleRecipientTypeChange(e.target.value as RecipientType)}
              className="form-control"
            >
              <option value="lender">Lender ({claim.craFinanceLender || 'Not specified'})</option>
              <option value="client">Client ({claim.email || 'No email'})</option>
              <option value="other">Other (Custom)</option>
            </select>
          </div>

          {/* Generate Only Option */}
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={generateOnly}
                onChange={(e) => setGenerateOnly(e.target.checked)}
              />
              <span>Generate letter for manual dispatch (don't send via email)</span>
            </label>
            <p className="form-hint">
              {generateOnly
                ? "The letter will be generated and saved to correspondence for you to download, print and mail manually."
                : "The letter will be emailed to the recipient and saved to correspondence."}
            </p>
          </div>

          {/* Recipient Email - only shown if not generate-only */}
          {!generateOnly && (
            <div className="form-group">
              <label htmlFor="recipient-email">Recipient Email *</label>
              <input
                id="recipient-email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                disabled={recipientType === 'client'}
                className="form-control"
                placeholder="recipient@example.com"
              />
            </div>
          )}

          {/* Recipient Name */}
          <div className="form-group">
            <label htmlFor="recipient-name">Recipient Name *</label>
            <input
              id="recipient-name"
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              className="form-control"
              placeholder="Recipient Name"
            />
          </div>

          {/* Recipient Address Line 1 */}
          <div className="form-group">
            <label htmlFor="recipient-address1">Address Line 1 *</label>
            <input
              id="recipient-address1"
              type="text"
              value={recipientAddressLine1}
              onChange={(e) => setRecipientAddressLine1(e.target.value)}
              className="form-control"
              placeholder="123 Main Street"
            />
          </div>

          {/* Recipient Address Line 2 */}
          <div className="form-group">
            <label htmlFor="recipient-address2">Address Line 2</label>
            <input
              id="recipient-address2"
              type="text"
              value={recipientAddressLine2}
              onChange={(e) => setRecipientAddressLine2(e.target.value)}
              className="form-control"
              placeholder="City or additional address info"
            />
          </div>

          {/* Recipient Postcode */}
          <div className="form-group">
            <label htmlFor="recipient-postcode">Postcode *</label>
            <input
              id="recipient-postcode"
              type="text"
              value={recipientPostcode}
              onChange={(e) => setRecipientPostcode(e.target.value)}
              className="form-control"
              placeholder="AB12 3CD"
            />
          </div>

          {/* Letter Content */}
          <div className="form-group">
            <label htmlFor="letter-content">Letter Content *</label>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: '6px',
              }}
            >
              <ComplexAIButton
                draftType="letter"
                claim={claim}
                existingBody={letterContent}
                disabled={sending}
                onInsert={(draftText) => insertMergeField(draftText)}
                onReplace={(draftText) => setLetterContent(draftText)}
              />
            </div>

            <div className="merge-fields-panel">
              <p className="merge-fields-hint">Click a field to insert it at cursor position:</p>
              <div className="merge-fields-grid">
                {MERGE_FIELDS.map((field) => (
                  <button
                    key={field.value}
                    type="button"
                    className="merge-field-button"
                    onClick={() => insertMergeField(field.value)}
                    title={field.description}
                  >
                    {field.label}
                  </button>
                ))}
              </div>
            </div>

            <textarea
              id="letter-content"
              value={letterContent}
              onChange={(e) => setLetterContent(e.target.value)}
              className="form-control letter-content-textarea"
              rows={12}
              placeholder="Dear {RecipientName},

I am writing to you regarding..."
            />
            <p className="form-hint">Click the merge fields above to insert claim-specific data</p>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => handleSend(generateOnly)} disabled={sending}>
            {sending
              ? generateOnly ? 'Generating...' : 'Sending...'
              : generateOnly ? 'Generate Letter' : 'Send Letter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendLetterModal;
